<template>
    <div class="page-container role-form-create">
        <!-- Page Header -->
        <div class="page-header-wrapper" :class="{ 'is-scrolled': isScrolled }">
            <div class="content-wrapper">
                <div class="page-header">
                    <div class="header-left">
                        <div>
                            <h1 class="page-title">{{ page.cardTitle }}</h1>
                            <p class="page-subtitle">Create and configure role permissions</p>
                        </div>
                    </div>
                    <div class="header-actions">
                        <button type="button" class="btn-cancel" @click="cancelForm" :disabled="page.isLoading">Cancel</button>
                        <button type="button" class="btn-save" @click="change" :disabled="page.isLoading || !role.name">
                            <span v-if="page.isLoading"><i class="fas fa-spinner fa-spin me-2"></i> Saving...</span>
                            <span v-else><i class="fas fa-lock me-2"></i> Save Role</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div class="content-wrapper mt-4">
            <div class="main-grid">
                <!-- Left Column -->
                <div class="grid-column main-col">
                    <div class="content-card">
                        <!-- 1. Role Information -->
                        <div class="card-section">
                            <div class="section-header">
                                <span class="section-number">01</span>
                                <div>
                                    <label class="section-label">Role Information</label>
                                    <p class="section-desc">Define basic information for this role.</p>
                                </div>
                            </div>

                            <div class="form-grid section-content mt-3">
                                <div class="form-row">
                                    <div class="form-sub-item">
                                        <label>Role Name <span class="required">*</span></label>
                                        <input
                                            autocomplete="off"
                                            v-model="role.name"
                                            type="text"
                                            class="modern-input"
                                            :class="{ 'is-invalid border-danger': validation.name }"
                                            placeholder="Enter role name (e.g. Admin Merchant)"
                                            maxlength="150"
                                        />
                                        <div class="d-flex justify-content-between align-items-start mt-1">
                                            <div v-if="validation.name" class="text-danger small fw-bold">
                                                <i class="fas fa-exclamation-circle me-1"></i> {{ validation.name[0] }}
                                            </div>
                                            <div v-else></div>
                                            <small class="char-counter text-muted" style="font-size: 10px;">{{ (role.name || '').length }}/150 characters</small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- 2. Configure Permissions -->
                        <div class="card-section border-top pt-4 mt-4">
                            <div class="section-header">
                                <span class="section-number">02</span>
                                <div>
                                    <label class="section-label">Configure Permissions</label>
                                    <p class="section-desc">Choose a template to quickly apply recommended settings, or configure permissions manually.</p>
                                </div>
                            </div>

                            <div v-if="configMode === 'template'">
                                <div class="templates-grid section-content">
                                <!-- Template: Admin Merchant -->
                                <div class="template-card" :class="{'selected': selectedTemplate === 'admin-merchant'}" @click="applyTemplate('admin-merchant')">
                                    <div class="template-badge">
                                        APPLIED
                                    </div>
                                    <div class="template-header">
                                        <div class="template-icon" :style="getTemplateIconStyle('admin-merchant')">
                                            <i class="fas fa-store"></i>
                                        </div>
                                        <h4 class="template-name">Merchant</h4>
                                    </div>
                                    <p class="template-desc">Manages all merchant operations including members, transactions, products, vouchers, and campaigns. No access to system configuration or corporate settings.</p>
                                    <ul class="template-features">
                                        <li><i class="fas fa-check text-primary"></i> Manage Members</li>
                                        <li><i class="fas fa-check text-primary"></i> Manage Transactions</li>
                                        <li><i class="fas fa-check text-primary"></i> Manage Products</li>
                                        <li><i class="fas fa-check text-primary"></i> Manage Vouchers & Reedems</li>
                                        <li><i class="fas fa-eye text-primary"></i> View Campaigns</li>
                                        <li><i class="fas fa-times text-danger"></i> System Configuration</li>
                                        <li><i class="fas fa-times text-danger"></i> Corporate Settings</li>
                                    </ul>
                                </div>
                                
                                <!-- Template: Admin Branch -->
                                <div class="template-card" :class="{'selected': selectedTemplate === 'admin-branch'}" @click="applyTemplate('admin-branch')">
                                    <div class="template-badge">
                                        APPLIED
                                    </div>
                                    <div class="template-header">
                                        <div class="template-icon" :style="getTemplateIconStyle('admin-branch')">
                                            <i class="fas fa-building"></i>
                                        </div>
                                        <h4 class="template-name">Branch / Unit</h4>
                                    </div>
                                    <p class="template-desc">Manages branch or unit operations. Able to manage members, transactions, and report of each merchant within the branch scope, but cannot modify corporate configurations.</p>
                                    <ul class="template-features">
                                        <li><i class="fas fa-check text-primary"></i> Manage Members</li>
                                        <li><i class="fas fa-check text-primary"></i> Manage Voucher & Redemption on Merchant</li>
                                        <li><i class="fas fa-check text-primary"></i> Branch Products & Marketings</li>
                                        <li><i class="fas fa-eye text-primary"></i> View Report of Each Merchants</li>
                                        <li><i class="fas fa-times text-danger"></i> Corporate Settings</li>
                                        <li><i class="fas fa-times text-danger"></i> Cross-Branch Access</li>
                                    </ul>
                                </div>
                                
                                <!-- Template: Admin Corporate -->
                                <div class="template-card" :class="{'selected': selectedTemplate === 'admin-corporate'}" @click="applyTemplate('admin-corporate')">
                                    <div class="template-badge">
                                        APPLIED
                                    </div>
                                    <div class="template-header">
                                        <div class="template-icon" :style="getTemplateIconStyle('admin-corporate')">
                                            <i class="fas fa-city"></i>
                                        </div>
                                        <h4 class="template-name">Corporate</h4>
                                    </div>
                                    <p class="template-desc">Corporate-level administrator with full control over the entire system, all branches, configurations, and user management.</p>
                                    <ul class="template-features">
                                        <li><i class="fas fa-check text-primary"></i> All Modules</li>
                                        <li><i class="fas fa-check text-primary"></i> User Role Management</li>
                                        <li><i class="fas fa-check text-primary"></i> System Configuration</li>
                                        <li><i class="fas fa-check text-primary"></i> Organization Reports</li>
                                        <li><i class="fas fa-check text-primary"></i> Multi-Branch Management</li>
                                    </ul>
                                </div>
                                </div>
                                <div class="text-center mt-4">
                                    <p class="text-muted small mb-2">Need more specific access control?</p>
                                    <button type="button" class="btn-custom-outline" @click="switchToManualConfig">
                                        <i class="fas fa-sliders-h me-2"></i> Configure Permissions Manually
                                    </button>
                                </div>
                            </div>

                            <div v-if="configMode === 'manual'" class="section-content mt-3 permissions-table-wrapper">
                                <div class="table-actions d-flex justify-content-between align-items-center mb-3 flex-wrap gap-3">
                                    <div class="d-flex align-items-center gap-3 flex-wrap">
                                        <button type="button" class="btn-fintech-back" @click="configMode = 'template'">
                                            <i class="fas fa-arrow-left"></i>
                                            <span>Use Templates</span>
                                        </button>
                                        <div class="search-box">
                                            <i class="fas fa-search"></i>
                                            <input type="text" placeholder="Search module..." v-model="searchModule">
                                        </div>
                                    </div>
                                    <div class="d-flex gap-2 flex-wrap align-items-center">
                                        <button type="button" class="btn btn-action btn-bulk-read" @click="setAllToRead">
                                            <i class="fas fa-book-reader me-1"></i> All Read
                                        </button>
                                        <button type="button" class="btn btn-action btn-bulk-write" @click="setAllToWrite">
                                            <i class="fas fa-edit me-1"></i> All Write
                                        </button>
                                        <button type="button" class="btn btn-action btn-bulk-clear" @click="setAllToNone" title="Clear all permissions">
                                            <i class="fas fa-times me-1"></i> Clear All
                                        </button>
                                    </div>
                                </div>
                                
                                <div v-if="validation.permissions" class="text-danger small mb-3 mt-n2 ms-2 fw-bold">
                                    <i class="fas fa-exclamation-circle me-1"></i> {{ validation.permissions[0] }}
                                </div>

                                <div class="permissions-list">
                                    <div v-for="module in filteredModules" :key="module.value" class="permission-item">
                                        <div class="module-info">
                                            <div class="module-icon" :class="getModuleIconClass(module.text)">
                                                <i class="fas" :class="getModuleIcon(module.text)"></i>
                                            </div>
                                            <div>
                                                <h5 class="module-title">{{ formattedModuleName(module.text) }}</h5>
                                                <p class="module-desc">{{ getModuleDesc(module.text) }}</p>
                                            </div>
                                        </div>
                                        <div class="permission-controls">
                                            <div class="segmented-control">
                                                <button type="button" 
                                                    :class="['segment-btn', 'btn-none', { active: role.permissions[module.value]?.none || !role.permissions[module.value] }]"
                                                    @click="setAbilityManual(module.value, 'none')">None</button>
                                                <button type="button"
                                                    :class="['segment-btn', 'btn-read', { active: role.permissions[module.value]?.read }]"
                                                    @click="setAbilityManual(module.value, 'read')">Read</button>
                                                <button type="button" v-if="module.text !== 'transaction-report'"
                                                    :class="['segment-btn', 'btn-write', { active: role.permissions[module.value]?.write }]"
                                                    @click="setAbilityManual(module.value, 'write')">Write</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                <div class="d-flex justify-content-center my-4" v-if="page.isLoading">
                                    <div class="spinner-border text-primary" role="status"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Right Column (Sidebar) -->
                <div class="grid-column side-col">
                    <div class="content-card summary-card modern-fintech-card p-0">
                        <div class="summary-header">
                            <div class="icon-circle">
                                <i class="fas fa-file-invoice"></i>
                            </div>
                            <h3 class="section-title mb-0" style="border: none; padding: 0;">Role Summary</h3>
                        </div>
                        
                        <div class="summary-body">
                            <div class="summary-block">
                                <h6 class="summary-block-title mb-3">Permission Overview</h6>
                                <div class="stats-row">
                                    <div class="stat-box">
                                        <span class="stat-value text-none-status">{{ overviewStats.none }}</span>
                                        <span class="stat-label">None</span>
                                    </div>
                                    <div class="stat-box">
                                      <span class="stat-value text-read-status">{{ overviewStats.read }}</span>
                                      <span class="stat-label">Read</span>
                                    </div>
                                    <div class="stat-box">
                                        <span class="stat-value text-write-status">{{ overviewStats.write }}</span>
                                        <span class="stat-label">Write</span>
                                    </div>
                                </div>
                            </div>
                            
                            <div class="summary-block mt-4">
                                <div class="d-flex justify-content-between align-items-center mb-2">
                                    <h6 class="summary-block-title mb-0">Accessible Modules ({{ accessibleModules.length }})</h6>
                                </div>
                                <ul class="module-list success-list">
                                    <li v-for="mod in (showAllAccessible ? accessibleModules : accessibleModules.slice(0, 5))" :key="mod.value">
                                        <i class="fas fa-check text-primary"></i> 
                                        {{ formattedModuleName(mod.text) }}
                                        <span class="mini-badge" :class="role.permissions[mod.value]?.write ? 'badge-w' : 'badge-r'">
                                            {{ role.permissions[mod.value]?.write ? 'W' : 'R' }}
                                        </span>
                                    </li>
                                </ul>
                                <div v-if="!showAllAccessible && accessibleModules.length > 5" class="text-primary small fw-bold mt-2 cursor-pointer" @click="showAllAccessible = true">
                                    + {{ accessibleModules.length - 5 }} more modules
                                </div>
                                <div v-if="showAllAccessible && accessibleModules.length > 5" class="text-primary small fw-bold mt-2 cursor-pointer" @click="showAllAccessible = false">
                                    Show less
                                </div>
                            </div>
                            
                            <div class="summary-block mt-4 border-top pt-3">
                                <div class="d-flex justify-content-between align-items-center mb-2">
                                    <h6 class="summary-block-title mb-0">Restricted Modules ({{ restrictedModules.length }})</h6>
                                </div>
                                <ul class="module-list danger-list">
                                    <li v-for="mod in (showAllRestricted ? restrictedModules : restrictedModules.slice(0, 5))" :key="mod.value">
                                        <i class="fas fa-times"></i> {{ formattedModuleName(mod.text) }}
                                    </li>
                                </ul>
                                <div v-if="!showAllRestricted && restrictedModules.length > 5" class="text-danger small fw-bold mt-2 cursor-pointer" @click="showAllRestricted = true">
                                    + {{ restrictedModules.length - 5 }} more modules
                                </div>
                                <div v-if="showAllRestricted && restrictedModules.length > 5" class="text-danger small fw-bold mt-2 cursor-pointer" @click="showAllRestricted = false">
                                    Show less
                                </div>
                            </div>
                        </div>
                        
                        <div class="summary-footer bg-light-blue p-3 m-3 rounded">
                            <p class="small text-muted mb-0 d-flex align-items-start gap-2">
                                <i class="fas fa-info-circle text-primary"></i>
                                <span>Changes made to permissions will be reflected in the summary.</span>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { reactive, ref, onMounted, onUnmounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import store from '@/store';
import { useToast } from '@/composables/useToast';

export default {
  name: 'RoleForm',
  setup() {
    const { showToast } = useToast();
    const router = useRouter();
    const route = useRoute();

    const page = reactive({
      isLoading: false,
      cardTitle: 'Create User Role',
    });

    const role = reactive({
      id: '',
      name: '',
      permissions: {},
      corporate_id: '',
    });

    const validation = ref([]);
    const isScrolled = ref(false);
    
    // Template states
    const selectedTemplate = ref(null);
    const isCustomized = ref(false);
    const configMode = ref('template');
    const searchModule = ref('');
    const showAllAccessible = ref(false);
    const showAllRestricted = ref(false);

    const modules = computed(() => 
        store.state.Module.modules
            .map(module => ({ value: module.id, text: module.name }))
            .sort((a, b) => a.text.localeCompare(b.text))
    );

    const filteredModules = computed(() => {
        if (!searchModule.value) return modules.value;
        const q = searchModule.value.toLowerCase();
        return modules.value.filter(m => formattedModuleName(m.text).toLowerCase().includes(q));
    });

    const handleScroll = () => {
        isScrolled.value = window.scrollY > 20;
    };

    onMounted(async() => {
        window.scrollTo(0, 0);
        window.addEventListener('scroll', handleScroll);
        
        page.isLoading = true;
        try {
            await loadModules();
            if (route.params.id) {
                await loadState(route.params.id);
            }
        } catch (error) {
            console.error(error);
        } finally {
            page.isLoading = false;
        }
    });

    onUnmounted(() => {
        window.removeEventListener('scroll', handleScroll);
    });

    async function loadState(id) {
      store.dispatch('Auth/getBusinessProfile').then((businessProfile) => {
        store.dispatch('Role/read_single', { id, corporate_id: businessProfile.corporate_id }).then((data) => {
          const first = data[Object.keys(data)[0]];

          role.id = id;
          role.name = first.name;
          role.corporate_id = first.corporate_id;
          role.permissions = first.modules.reduce((acc, module) => {
            acc[module.id] = {
              id: module.id,
              none: module.none === 1,
              read: module.read === 1,
              write: module.write === 1
            };
            return acc;
          }, {});

          page.cardTitle = 'Edit User Roles';
          // Check if it matches any templates
          updateTemplateStatus();
          if (isCustomized.value) {
              configMode.value = 'manual';
          }
        });
      });
    }

    async function loadModules() {
        return store.dispatch('Module/read');
    }

    function setAbility(moduleValue, ability) {
      if (!role.permissions[moduleValue]) {
        role.permissions[moduleValue] = {};
      }
      role.permissions[moduleValue] = { id: moduleValue, none: false, read: false, write: false };
      role.permissions[moduleValue][ability] = true;
    }

    function setAbilityManual(moduleValue, ability) {
      setAbility(moduleValue, ability);
      updateTemplateStatus();
    }

    function setAllToNone() {
      modules.value.forEach(module => {
        if (!role.permissions[module.value]) {
          role.permissions[module.value] = {};
        }
        role.permissions[module.value] = { id: module.value, none: true };
      });
      updateTemplateStatus();
    }

    function switchToManualConfig() {
      configMode.value = 'manual';
    }

    function setAllToRead() {
      modules.value.forEach(module => {
        setAbility(module.value, 'read');
      });
      updateTemplateStatus();
    }

    function setAllToWrite() {
      modules.value.forEach(module => {
        if (module.text !== 'transaction-report') {
            setAbility(module.value, 'write');
        } else {
            setAbility(module.value, 'read'); // transaction-report typically read-only
        }
      });
      updateTemplateStatus();
    }

    function applyTemplate(templateType) {
      selectedTemplate.value = templateType;
      isCustomized.value = false;
      
      const merchantWrite = ['register-option', 'point-manipulation', 'point-plan', 'product', 'voucher-redeem', 'voucher', 'transaction'];
      const merchantRead = ['benefit', 'member', 'newsletter', 'user-role', 'role', 'user-roles', 'campaign', 'merchant', 'user', 'member-tier', 'branch', 'promotion', 'promotions', 'policies', 'tier-duration'];
      
      const branchWrite = ['benefit', 'point-manipulation', 'point-plan', 'member', 'campaign', 'merchant', 'access-token', 'product', 'voucher-redeem', 'voucher', 'transaction'];
      const branchRead = ['user-role', 'role', 'user-roles', 'marketing', 'member-tier', 'branch', 'tier-duration'];
      
      const corporateNone = ['access-token'];
      const corporateRead = ['transaction-report'];

      modules.value.forEach(module => {
        // Normalize name to handle potential variations (e.g. 'User Roles' -> 'user-roles')
        const name = module.text.toLowerCase().replace(/_/g, '-').replace(/\s+/g, '-');
        let ability = 'none';
        
        if (templateType === 'admin-merchant') {
           if (merchantWrite.includes(name)) {
             ability = 'write';
           } else if (merchantRead.includes(name)) {
             ability = 'read';
           }
        } else if (templateType === 'admin-branch') {
           if (branchWrite.includes(name)) {
             ability = 'write';
           } else if (branchRead.includes(name)) {
             ability = 'read';
           }
        } else if (templateType === 'admin-corporate') {
           if (corporateNone.includes(name)) {
             ability = 'none';
           } else if (corporateRead.includes(name)) {
             ability = 'read';
           } else {
             ability = 'write';
           }
        }
        
        setAbility(module.value, ability);
      });
      updateTemplateStatus();
    }

    function detectTemplate() {
      const merchantWrite = ['register-option', 'point-manipulation', 'point-plan', 'product', 'voucher-redeem', 'voucher', 'transaction'];
      const merchantRead = ['benefit', 'member', 'newsletter', 'user-role', 'role', 'user-roles', 'campaign', 'merchant', 'user', 'member-tier', 'branch', 'promotion', 'promotions', 'policies', 'tier-duration'];
      
      const branchWrite = ['benefit', 'point-manipulation', 'point-plan', 'member', 'campaign', 'merchant', 'access-token', 'product', 'voucher-redeem', 'voucher', 'transaction'];
      const branchRead = ['user-role', 'role', 'user-roles', 'marketing', 'member-tier', 'branch', 'tier-duration'];
      
      const corporateNone = ['access-token'];
      const corporateRead = ['transaction-report'];

      let isMerchant = true;
      let isBranch = true;
      let isCorporate = true;

      modules.value.forEach(module => {
        const name = module.text.toLowerCase().replace(/_/g, '-').replace(/\s+/g, '-');
        
        const p = role.permissions[module.value];
        let ability = 'none';
        if (p) {
          if (p.write) ability = 'write';
          else if (p.read) ability = 'read';
        }
        
        let expMerchant = 'none';
        if (merchantWrite.includes(name)) expMerchant = 'write';
        else if (merchantRead.includes(name)) expMerchant = 'read';
        if (ability !== expMerchant) isMerchant = false;

        let expBranch = 'none';
        if (branchWrite.includes(name)) expBranch = 'write';
        else if (branchRead.includes(name)) expBranch = 'read';
        if (ability !== expBranch) isBranch = false;

        let expCorporate = 'write';
        if (corporateNone.includes(name)) expCorporate = 'none';
        else if (corporateRead.includes(name)) expCorporate = 'read';
        if (ability !== expCorporate) isCorporate = false;
      });

      if (isCorporate) return 'admin-corporate';
      if (isMerchant) return 'admin-merchant';
      if (isBranch) return 'admin-branch';
      return null;
    }

    function updateTemplateStatus() {
      const detected = detectTemplate();
      if (detected) {
        selectedTemplate.value = detected;
        isCustomized.value = false;
      } else {
        selectedTemplate.value = null;
        const allNone = modules.value.every(m => {
          const p = role.permissions[m.value];
          return !p || p.none;
        });
        isCustomized.value = !allNone;
      }
    }

    function cancelForm() {
      router.push({ name: 'Role' });
    }

    function change() {
      const data = JSON.parse(JSON.stringify(role));
      
      if (data.permissions) {
          Object.keys(data.permissions).forEach(key => {
              if (data.permissions[key].none || (!data.permissions[key].read && !data.permissions[key].write)) {
                  delete data.permissions[key];
              }
          });
      }

      page.isLoading = true;
      store.dispatch('Auth/getBusinessProfile').then((businessProfile) => {
          data.corporate_id = businessProfile.corporate_id;

          if (route.name !== 'RoleForm') {
            store.dispatch('Role/update', { data })
              .then(() => {
                showToast({
                    type: 'success',
                    title: 'Updated',
                    message: `Role "${data.name.length > 25 ? data.name.substring(0, 25) + '..' : data.name}" has been updated successfully.`
                });
                router.push({ name: 'Role' });
              })
              .catch((err) => {
                console.log(err);
                if(err.response && err.response.data && err.response.data.error) {
                   validation.value = JSON.parse(err.response.data.error);
                   if (validation.value.permissions) {
                       validation.value.permissions[0] = "Please select a template or configure at least one permission manually.";
                   }
                   const firstErrorKey = Object.keys(validation.value)[0];
                   if (firstErrorKey) {
                       showToast({
                           type: 'error',
                           title: 'Validation Error',
                           message: validation.value[firstErrorKey][0]
                       });
                   }
                }
              })
              .finally(() => {
                page.isLoading = false;
              });
          } else {
            store.dispatch('Role/create', { data })
              .then(() => {
                showToast({
                    type: 'success',
                    title: 'Created',
                    message: `Role "${data.name.length > 25 ? data.name.substring(0, 25) + '..' : data.name}" has been created successfully.`
                });
                router.push({ name: 'Role', query: { newRole: 'true' } });
              })
              .catch((err) => {
                if(err.response && err.response.data && err.response.data.error) {
                   validation.value = JSON.parse(err.response.data.error);
                   if (validation.value.permissions) {
                       validation.value.permissions[0] = "Please select a template or configure at least one permission manually.";
                   }
                   const firstErrorKey = Object.keys(validation.value)[0];
                   if (firstErrorKey) {
                       showToast({
                           type: 'error',
                           title: 'Validation Error',
                           message: validation.value[firstErrorKey][0]
                       });
                   }
                } else {
                   console.log(err);
                }
              })
              .finally(() => {
                page.isLoading = false;
              });
          }
      });
    }

    const formattedModuleName = (name) => {
      return name
          .replace(/[-_]/g, ' ')
          .replace(/\b\w/g, char => char.toUpperCase());
    }

    // Helper functions for UI
    const overviewStats = computed(() => {
       let write = 0, read = 0, none = 0;
       modules.value.forEach(module => {
           const p = role.permissions[module.value];
           if (!p || p.none) none++;
           else if (p.write) write++;
           else if (p.read) read++;
           else none++;
       });
       return { write, read, none };
    });
    
    const accessibleModules = computed(() => {
       return modules.value.filter(module => {
           const p = role.permissions[module.value];
           return p && (p.write || p.read);
       });
    });

    const restrictedModules = computed(() => {
       return modules.value.filter(module => {
           const p = role.permissions[module.value];
           return !p || p.none;
       });
    });

    const getTemplateName = (type) => {
        const names = {
            'admin-merchant': 'Merchant',
            'admin-branch': 'Branch / Unit',
            'admin-corporate': 'Corporate'
        };
        return names[type] || 'Unknown';
    };

    const getTemplateIcon = (type) => {
        const icons = {
            'admin-merchant': 'fa-store',
            'admin-branch': 'fa-building',
            'admin-corporate': 'fa-city'
        };
        return icons[type] || 'fa-user-tag';
    };

    const getTemplateIconStyle = (type) => {
        const colors = {
            'admin-merchant': 'var(--bottom)',
            'admin-branch': '#3b82f6',
            'admin-corporate': 'var(--primary-color)'
        };
        const color = colors[type] || 'var(--secondary-color, #94a3b8)';
        return `color: ${color}; background-color: color-mix(in srgb, ${color} 20%, transparent);`;
    };

    const getModuleIcon = (name) => {
        name = name.toLowerCase();
        if(name.includes('branch')) return 'fa-building';
        if(name.includes('merchant')) return 'fa-store';
        if(name.includes('member-tier') || name.includes('tier')) return 'fa-crown';
        if(name.includes('member')) return 'fa-users';
        if(name.includes('user-role') || name.includes('role')) return 'fa-user-shield';
        if(name.includes('user')) return 'fa-user-tie';
        if(name.includes('product')) return 'fa-box';
        if(name.includes('transaction-report') || name.includes('report')) return 'fa-chart-line';
        if(name.includes('transaction')) return 'fa-file-invoice-dollar';
        if(name.includes('voucher-redeem') || name.includes('redeem')) return 'fa-exchange-alt';
        if(name.includes('voucher')) return 'fa-ticket-alt';
        if(name.includes('point')) return 'fa-coins';
        if(name.includes('register')) return 'fa-clipboard-list';
        if(name.includes('token') || name.includes('access')) return 'fa-key';
        if(name.includes('benefit')) return 'fa-award';
        if(name.includes('export')) return 'fa-file-export';
        if(name.includes('tax')) return 'fa-receipt';
        if(name.includes('marketing')) return 'fa-bullseye';
        if(name.includes('campaign')) return 'fa-bullhorn';
        if(name.includes('policy') || name.includes('policies')) return 'fa-file-contract';
        if(name.includes('promotion')) return 'fa-tags';
        if(name.includes('newsletter')) return 'fa-envelope-open-text';
        if(name.includes('system') || name.includes('config')) return 'fa-cog';
        return 'fa-layer-group';
    };

    const getModuleIconClass = () => {
        return 'module-icon-theme';
    };

    const getModuleDesc = (name) => {
        name = name.toLowerCase();
        if(name.includes('member')) return 'Manage member, profile and data';
        if(name.includes('transaction')) return 'View and manage member transactions';
        if(name.includes('promotion') || name.includes('campaign')) return 'Create and manage promotions and campaigns';
        if(name.includes('product') || name.includes('voucher')) return 'Manage vouchers and reward';
        if(name.includes('report')) return 'View activity reports and analytics';
        if(name.includes('system') || name.includes('config')) return 'Manage system settings and configurations';
        if(name.includes('role')) return 'Manage user roles and their permissions';
        return `Manage ${formattedModuleName(name).toLowerCase()} settings`;
    };

    return {
      role,
      validation,
      modules,
      filteredModules,
      searchModule,
      page,
      isScrolled,
      selectedTemplate,
      isCustomized,
      showAllAccessible,
      showAllRestricted,
      configMode,
      overviewStats,
      accessibleModules,
      restrictedModules,
      change,
      cancelForm,
      setAbilityManual,
      setAllToNone,
      setAllToRead,
      setAllToWrite,
      applyTemplate,
      switchToManualConfig,
      formattedModuleName,
      getTemplateName,
      getTemplateIcon,
      getTemplateIconStyle,
      getModuleIcon,
      getModuleIconClass,
      getModuleDesc
    };
  },
};
</script>

<style scoped>
/* Page Layout */
.page-container {
    width: 100%;
    min-height: calc(100vh - 64px);
    background-color: #f1f4f9;
    padding: 24px 40px;
}

.content-wrapper {
    max-width: 1240px;
    margin: 0 auto;
}

/* Page Header Wrapper */
.page-header-wrapper {
    position: sticky;
    top: 0;
    z-index: 99;
    margin: -24px -40px 16px -40px;
    padding: 24px 40px 0 40px;
    background: rgba(241, 244, 249, 0);
    border-bottom: 1px solid rgba(0, 0, 0, 0);
    box-shadow: 0 10px 20px 5px rgba(241, 244, 249, 0);
    backdrop-filter: blur(0px);
    -webkit-backdrop-filter: blur(0px);
    transition: background 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease, backdrop-filter 0.3s ease;
}

.page-header-wrapper.is-scrolled {
    background: rgba(241, 244, 249, 0.85);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border-bottom: 1px solid rgba(0, 0, 0, 0.03);
    box-shadow: 0 10px 20px 5px rgba(241, 244, 249, 0.5);
}

/* Page Header */
.page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
    padding-bottom: 16px;
}

.header-left {
    display: flex;
    align-items: center;
    gap: 16px;
}

.page-title {
    font-size: 24px;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: -0.5px;
    margin: 0;
}

.page-subtitle {
    color: #64748b;
    font-size: 13px;
    margin: 4px 0 0 0;
}

.header-actions {
    display: flex;
    align-items: center;
    gap: 12px;
}

/* Buttons */
.btn-cancel {
    padding: 10px 24px;
    border: 1px solid #e2e8f0;
    background: white;
    color: #475569;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s, border-color 0.2s;
}

.btn-cancel:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
}

.btn-save {
    padding: 10px 24px;
    background: var(--primary-color, #F93E3E);
    border: none;
    color: white;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.3s ease, box-shadow 0.3s ease, filter 0.3s ease;
    display: flex;
    align-items: center;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.btn-save:hover:not(:disabled) {
    filter: brightness(0.9);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.15);
}

.btn-save:disabled {
    background: #cbd5e1;
    box-shadow: none;
    cursor: not-allowed;
    opacity: 0.7;
}

/* Grid Layout */
.main-grid {
    display: grid;
    grid-template-columns: 1fr 320px;
    gap: 24px;
    align-items: start;
}

@media (max-width: 992px) {
    .main-grid {
        grid-template-columns: 1fr;
    }
}

/* Content Cards */
.content-card {
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    padding: 28px 32px;
    border: 1px solid rgba(0, 0, 0, 0.05);
}

.card-section {
    margin-bottom: 0;
}

/* Section Header (Numbering Style) */
.section-header {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 1.25rem;
}

.section-number {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: color-mix(in srgb, var(--primary-color, #F93E3E), white 88%);
    color: var(--primary-color, #F93E3E);
    font-size: 0.75rem;
    font-weight: 700;
    flex-shrink: 0;
    margin-top: 2px;
}

.section-label {
    display: block;
    font-size: 1.05rem;
    font-weight: 700;
    color: #111827;
    margin-bottom: 0.2rem;
}

.section-title {
    font-size: 17px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 4px;
}

.section-desc {
    font-size: 0.85rem;
    color: #64748b;
    margin: 0;
}

.section-content {
    margin-left: 48px;
}

/* Form Styling */
.form-row {
    display: flex;
    gap: 24px;
}

.form-sub-item {
    flex: 1;
}

.form-sub-item label {
    display: block;
    font-size: 12px;
    font-weight: 700;
    color: #475569;
    margin-bottom: 8px;
}

.required {
    color: #ef4444;
}

.modern-input {
    width: 100%;
    padding: 12px 16px;
    border: 1.5px solid #e2e8f0;
    border-radius: 8px;
    font-size: 14px;
    color: #0f172a;
    background-color: #f8fafc;
    transition: all 0.2s;
}

.modern-input:focus {
    border-color: var(--primary-color, #F93E3E);
    background-color: #ffffff;
    box-shadow: 0 0 0 2px var(--secondary-color, #Fee9E9);
    outline: none;
}

/* Templates Grid */
.templates-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
}

@media (max-width: 1200px) {
    .templates-grid {
        grid-template-columns: 1fr;
    }
}

.template-card {
    border: 1.5px solid #e2e8f0;
    border-radius: 15px;
    padding: 20px;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    background: white;
    transform: translateY(0);
}

.template-card:hover {
    border-color: #cbd5e1;
    background: #ffffff;
    transform: translateY(-4px);
    box-shadow: 0 12px 20px -8px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.05);
}

.template-card.selected {
    border-color: var(--primary-color, #3b82f6);
    background: var(--secondary-color, #f0f9ff);
    transform: translateY(-4px) scale(1.02);
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.04);
}

.template-badge {
    position: absolute;
    top: -2px;
    right: -2px;
    background: var(--primary-color, #10b981);
    color: white;
    font-size: 9px;
    font-weight: 700;
    padding: 4px 10px;
    border-radius: 0 12px 0 12px;
    text-transform: uppercase;
    opacity: 0;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    letter-spacing: 0.5px;
    z-index: 10;
}

.template-card.selected .template-badge {
    opacity: 1;
}

.template-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
}

.template-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
}

.template-name {
    font-size: 15px;
    font-weight: 700;
    color: #1e293b;
    margin: 0;
}

.template-desc {
    font-size: 12px;
    color: #64748b;
    margin: 8px 0 16px 0;
    line-height: 1.5;
}

.template-features {
    list-style: none;
    padding: 0;
    margin: 0;
}

.template-features li {
    font-size: 12px;
    color: #475569;
    margin-bottom: 8px;
    display: flex;
    align-items: flex-start;
    gap: 8px;
}

.template-features li i {
    margin-top: 3px;
    font-size: 10px;
}

/* Utilities */
.bg-danger-soft { background: #fee2e2; }
.bg-success-soft { background: #dcfce7; }
.bg-purple-soft { background: #f3e8ff; }
.bg-warning-soft { background: #fef3c7; }
.bg-orange-soft { background: #ffedd5; }
.bg-info-soft { background: #e0f2fe; }
.bg-secondary-soft { background: #f1f5f9; }

.text-purple { color: #9333ea; }
.text-orange { color: #ea580c; }
.text-info { color: #0284c7; }
.text-warning { color: #bd7903 !important; }
.text-primary { color: var(--primary-color, #3b82f6); }
.text-danger { color: #ef4444; }

.text-none-status { color: #94a3b8 !important; }
.text-read-status { color: #3b82f6 !important; }
.text-write-status { color: var(--primary-color, #0d6efd) !important; }
.text-no-template { color: #475569 !important; font-weight: 600 !important; }

/* Permissions Table */
.search-box {
    position: relative;
    width: 250px;
}

.search-box i {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
}

.search-box input {
    width: 100%;
    padding: 8px 12px 8px 36px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 13px;
}

.search-box input:focus {
    outline: none;
    border-color: #cbd5e1;
}

/* Action Buttons */
.btn-action {
    padding: 6px 14px;
    font-size: 12px;
    font-weight: 600;
    border-radius: 8px;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    text-decoration: none;
    border: none;
    cursor: pointer;
}

.btn-bulk-read {
    background-color: #eff6ff;
    color: #1e40af;
}
.btn-bulk-read:hover {
    background-color: #dbeafe;
    color: #1e3a8a;
}
.btn-bulk-write {
    background-color: var(--secondary-color, #f8fafc);
    color: var(--primary-color, #0d6efd);
}
.btn-bulk-write:hover {
    background-color: var(--primary-color, #0d6efd);
    color: #ffffff;
}
.btn-bulk-clear {
    background-color: #f1f5f9;
    color: #475569;
    border: 1px solid #e2e8f0;
}
.btn-bulk-clear:hover {
    background-color: #e2e8f0;
    color: #1e293b;
}

.permissions-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.permission-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    background: #f8fafc;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
}

.module-info {
    display: flex;
    align-items: center;
    gap: 16px;
}

.module-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
}

.module-title {
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 4px 0;
}

.module-desc {
    font-size: 12px;
    color: #64748b;
    margin: 0;
}

.segmented-control {
    display: flex;
    background: #e2e8f0;
    padding: 3px;
    border-radius: 8px;
}

.segment-btn {
    padding: 6px 16px;
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
    background: transparent;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s;
}

.segment-btn.active {
    background: white;
    color: #1e293b;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.segment-btn:hover:not(.active) {
    color: #1e293b;
}

.segment-btn.btn-none.active { color: #64748b; }
.segment-btn.btn-read.active { color: #3b82f6; }
.segment-btn.btn-write.active { color: var(--primary-color, #0d6efd); }

/* Summary Sidebar */
.summary-card {
    position: sticky;
    top: 100px;
}

/* Modern Fintech Summary Card Override */
.modern-fintech-card {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    box-shadow: 0 10px 40px -10px rgba(0,0,0,0.06);
    overflow: hidden;
    position: sticky;
    top: 90px;
    z-index: 10;
}

.summary-header {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    padding: 16px 20px;
    display: flex;
    align-items: center;
    gap: 12px;
    border-bottom: 1px solid #e2e8f0;
}

.summary-header .icon-circle {
    width: 36px;
    height: 36px;
    background: var(--secondary-color, #FEE9E9);
    color: var(--primary-color, #F93E3E);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
}

.summary-body {
    padding: 24px;
}

.summary-block-title {
    font-size: 12px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 12px;
}

.empty-template-text {
    font-size: 12px;
    color: #94a3b8;
}

.summary-hint-text {
    font-size: 11px;
    color: #94a3b8;
}

.fintech-badge {
    padding: 4px 10px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.bg-theme-original {
    background-color: var(--secondary-color, #f1f5f9);
    color: var(--primary-color, #3b82f6);
}

.module-icon-theme {
    background-color: color-mix(in srgb, var(--primary-color) 5%, transparent) !important;
    color: color-mix(in srgb, var(--primary-color) 50%, transparent) !important;
}

.selected-template-display {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 12px;
}

.selected-template-display.empty {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    background: #f1f5f9;
    border: 1px dashed #cbd5e1;
}

.selected-template-display .template-icon {
    width: 32px;
    height: 32px;
    margin-bottom: 0;
    font-size: 14px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.stats-row {
    display: flex;
    gap: 12px;
}

.stat-box {
    flex: 1;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.stat-value {
    font-size: 20px;
    font-weight: 800;
    line-height: 1;
    margin-bottom: 4px;
}

.stat-label {
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
}

.module-list {
    list-style: none;
    padding: 0;
    margin: 0;
    font-size: 13px;
    color: #475569;
}

.module-list li {
    margin-bottom: 6px;
    display: flex;
    align-items: center;
    gap: 8px;
}

.success-list li i { color: #10b981; }
.danger-list li i { color: #ef4444; }

.bg-light-blue {
    background-color: color-mix(in srgb, var(--primary-color, #3b82f6), white 92%);
}
.cursor-pointer {
    cursor: pointer;
}

.mini-badge {
    font-size: 8px;
    font-weight: 600;
    padding: 2px 4px;
    border-radius: 4px;
    margin-left: 1px;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.mini-badge.badge-w {
    background-color: var(--primary-color, #0d6efd);
    color: white;
}

.mini-badge.badge-r {
    background-color: #3b82f6;
    color: white;
}

/* Custom Buttons */
.btn-custom-outline {
    color: var(--primary-color, #0d6efd);
    border: 1.5px solid var(--primary-color, #0d6efd);
    background-color: transparent;
    border-radius: 8px;
    padding: 8px 16px;
    font-size: 13px;
    font-weight: 600;
    transition: all 0.2s ease;
}
.btn-custom-outline:hover {
    background-color: var(--primary-color, #0d6efd);
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.btn-fintech-back {
    background-color: #ffffff;
    border: 1px solid #e2e8f0;
    color: #475569;
    font-weight: 600;
    font-size: 13px;
    padding: 8px 16px;
    border-radius: 8px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    gap: 8px;
}
.btn-fintech-back:hover {
    background-color: #f8fafc;
    border-color: #cbd5e1;
    color: var(--primary-color, #0d6efd);
    transform: translateY(-1px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}
</style>